import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import { IntegrationConfig } from 'src/api/surveyIntegration';
import { useTestSurveyIntegrationConnection } from 'src/queries/useSurveyIntegration';

interface ConnectionStatusSectionProps {
    config: IntegrationConfig;
}

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
}

function ConnectionStatusSection({ config }: ConnectionStatusSectionProps) {
    const testConnection = useTestSurveyIntegrationConnection();

    const isConnected = testConnection.isSuccess ? testConnection.data.valid : config.lastVerified;
    const workspaceName = testConnection.isSuccess
        ? testConnection.data.workspaceName ?? config.workspaceName
        : config.workspaceName;

    const renderTestResult = () => {
        if (testConnection.isLoading) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="spinner" spin className={styles.statusSpinner} />
                    <span>Testing connection…</span>
                </Flex>
            );
        }
        if (testConnection.isError) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="times-circle" className={styles.statusFailed} />
                    <span className={styles.statusFailed}>{errorMessage(testConnection.error)}</span>
                </Flex>
            );
        }
        if (testConnection.isSuccess) {
            const result = testConnection.data;
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon
                        type="fa"
                        name={result.valid ? 'check-circle' : 'times-circle'}
                        className={result.valid ? styles.statusVerified : styles.statusFailed}
                    />
                    <span className={result.valid ? styles.statusVerified : styles.statusFailed}>
                        {result.message}
                    </span>
                </Flex>
            );
        }
        return null;
    };

    return (
        <Card className={styles.cardWrap} label="Connection Status">
            <Flex direction="column" className={styles.sectionBody}>
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <div
                        className={classNames(styles.statusDot, {
                            [styles.statusDotConnected]: isConnected,
                            [styles.statusDotDisconnected]: !isConnected,
                        })}
                    />
                    <span className={styles.statusLabel}>{isConnected ? 'Connected' : 'Disconnected'}</span>
                </Flex>

                <Flex direction="column" className={styles.metaRow}>
                    <div>
                        <span className={styles.metaLabel}>Workspace</span>
                        {workspaceName ?? 'Not configured'}
                    </div>
                    <div>
                        <span className={styles.metaLabel}>Last verified</span>
                        {config.lastVerified ? 'Connection previously verified' : 'Never verified'}
                    </div>
                </Flex>

                <Flex direction="row" align="center" className={styles.buttonRow}>
                    <Button
                        type="secondary"
                        text="Test Connection"
                        isLoading={testConnection.isLoading}
                        onClick={() => testConnection.mutate()}
                    />
                </Flex>

                {renderTestResult()}
            </Flex>
        </Card>
    );
}

export default ConnectionStatusSection;
